/**
 * OrderBook Component
 * 
 * Market depth visualization
 * 
 * Version: 6.2.0
 */
import React, { useState, useEffect } from 'react'
import './OrderBook.css'

interface OrderLevel {
  price: number
  quantity: number
  orders: number
}

interface OrderBookProps {
  symbol: string
  bids?: OrderLevel[]
  asks?: OrderLevel[]
  showTotals?: boolean
  maxLevels?: number
  compact?: boolean
  onPriceClick?: (price: number, side: 'bid' | 'ask') => void
  className?: string
}

// Generate mock data for demo
const generateMockLevels = (basePrice: number, side: 'bid' | 'ask', count: number): OrderLevel[] => {
  const levels: OrderLevel[] = []
  const direction = side === 'bid' ? -1 : 1
  
  for (let i = 0; i < count; i++) {
    const priceOffset = (i + 1) * 0.05 * direction
    levels.push({
      price: basePrice + priceOffset,
      quantity: Math.floor(Math.random() * 500) + 50,
      orders: Math.floor(Math.random() * 10) + 1
    })
  }
  
  return levels
}

const OrderBook: React.FC<OrderBookProps> = ({
  symbol,
  bids,
  asks,
  showTotals = true,
  maxLevels = 10,
  compact = false,
  onPriceClick,
  className = ''
}) => {
  const [mockBids, setMockBids] = useState<OrderLevel[]>([])
  const [mockAsks, setMockAsks] = useState<OrderLevel[]>([])
  const basePrice = 24891.45

  // Generate mock data
  useEffect(() => {
    setMockBids(generateMockLevels(basePrice, 'bid', maxLevels))
    setMockAsks(generateMockLevels(basePrice, 'ask', maxLevels))
    
    // Simulate updates
    const interval = setInterval(() => {
      setMockBids(prev => prev.map(level => ({
        ...level,
        quantity: Math.floor(Math.random() * 500) + 50
      })))
      setMockAsks(prev => prev.map(level => ({
        ...level,
        quantity: Math.floor(Math.random() * 500) + 50
      })))
    }, 3000)
    
    return () => clearInterval(interval)
  }, [maxLevels])

  const displayBids = bids || mockBids
  const displayAsks = asks || mockAsks

  // Calculate max quantity for scaling
  const allQuantities = [...displayBids, ...displayAsks].map(l => l.quantity)
  const maxQuantity = Math.max(...allQuantities)

  // Calculate cumulative totals
  const bidsWithTotals = displayBids.reduce((acc, level, i) => {
    const prevTotal = i > 0 ? acc[i - 1].total : 0
    acc.push({ ...level, total: prevTotal + level.quantity })
    return acc
  }, [] as (OrderLevel & { total: number })[])

  const asksWithTotals = displayAsks.reduce((acc, level, i) => {
    const prevTotal = i > 0 ? acc[i - 1].total : 0
    acc.push({ ...level, total: prevTotal + level.quantity })
    return acc
  }, [] as (OrderLevel & { total: number })[])

  const formatPrice = (p: number) => p.toFixed(2)
  const formatQuantity = (q: number) => q.toLocaleString()

  const spread = displayAsks[0]?.price && displayBids[0]?.price 
    ? (displayAsks[0].price - displayBids[0].price).toFixed(2)
    : '0.00'

  return (
    <div className={`order-book ${compact ? 'compact' : ''} ${className}`}>
      {/* Header */}
      <div className="order-book-header">
        <h3 className="order-book-title">📊 {symbol}</h3>
        <div className="spread-info">
          <span className="spread-label">Spread</span>
          <span className="spread-value">₹{spread}</span>
        </div>
      </div>

      {/* Column Headers */}
      <div className="order-book-columns">
        <span>Price (₹)</span>
        <span>Qty</span>
        {showTotals && <span>Total</span>}
      </div>

      {/* Asks (Sell orders) - reversed so lowest is at bottom */}
      <div className="order-book-asks">
        {[...displayAsks].reverse().map((level, i) => {
          const depth = (level.quantity / maxQuantity) * 100
          const realIndex = displayAsks.length - 1 - i
          const total = asksWithTotals[realIndex]?.total || 0
          
          return (
            <div 
              key={`ask-${i}`}
              className="order-level ask"
              onClick={() => onPriceClick?.(level.price, 'ask')}
            >
              <div className="depth-bar ask" style={{ width: `${depth}%` }} />
              <span className="level-price">{formatPrice(level.price)}</span>
              <span className="level-quantity">{formatQuantity(level.quantity)}</span>
              {showTotals && <span className="level-total">{formatQuantity(total)}</span>}
            </div>
          )
        })}
      </div>

      {/* Spread Indicator */}
      <div className="order-book-spread">
        <span className="spread-mid">₹{basePrice.toFixed(2)}</span>
      </div>

      {/* Bids (Buy orders) */}
      <div className="order-book-bids">
        {displayBids.map((level, i) => {
          const depth = (level.quantity / maxQuantity) * 100
          const total = bidsWithTotals[i]?.total || 0
          
          return (
            <div 
              key={`bid-${i}`}
              className="order-level bid"
              onClick={() => onPriceClick?.(level.price, 'bid')}
            >
              <div className="depth-bar bid" style={{ width: `${depth}%` }} />
              <span className="level-price">{formatPrice(level.price)}</span>
              <span className="level-quantity">{formatQuantity(level.quantity)}</span>
              {showTotals && <span className="level-total">{formatQuantity(total)}</span>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default OrderBook